import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Users, Mail, Lock, LogIn, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CrewLogin = () => {
  const navigate = useNavigate();
  const [crewId, setCrewId] = useState('');
  const [accessCode, setAccessCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const buttonRef = useRef(null);

  const handleAccess = useCallback(async () => {
    if (!crewId || !accessCode) {
      toast.error('Please enter your crew ID and access code');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/crew/login`, { 
        email: crewId, 
        password: accessCode 
      });
      
      if (!response.data?.token) {
        throw new Error('Invalid response');
      }
      
      localStorage.setItem('crewToken', response.data.token);
      localStorage.setItem('crewInfo', JSON.stringify(response.data.crew || {}));
      localStorage.setItem('ownerInfo', JSON.stringify(response.data.owner || {}));
      
      toast.success(`Welcome, ${response.data.crew?.name || 'Crew Member'}!`);
      navigate('/crew-dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || error.message || 'Invalid credentials';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [crewId, accessCode, navigate]);

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) { toast.error('Please enter your email'); return; }
    setForgotLoading(true);
    try {
      const res = await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail.trim() });
      setForgotSent(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Request failed');
    } finally {
      setForgotLoading(false);
    }
  };

  // Handle Enter key press
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Enter' && crewId && accessCode) {
        e.preventDefault();
        e.stopPropagation();
        handleAccess();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [crewId, accessCode, handleAccess]);

  // Native click handler
  useEffect(() => {
    const btn = buttonRef.current;
    if (btn) {
      const clickHandler = (e) => {
        e.stopImmediatePropagation();
        e.preventDefault();
        handleAccess();
        return false;
      };
      btn.addEventListener('click', clickHandler, true);
      btn.addEventListener('mousedown', clickHandler, true);
      return () => {
        btn.removeEventListener('click', clickHandler, true);
        btn.removeEventListener('mousedown', clickHandler, true);
      };
    }
  }, [handleAccess]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Back to main login */}
        <Button 
          variant="ghost" 
          onClick={() => navigate('/login')}
          className="mb-6 text-stone-600"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Owner Login
        </Button>
        
        <div className="bg-white rounded-2xl border border-stone-200 shadow-lg p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
              <Users className="h-8 w-8 text-amber-600" />
            </div>
            <h1 className="text-2xl font-bold text-stone-800">Crew Access</h1>
            <p className="text-stone-600 mt-2">Enter your credentials to view tasks</p>
          </div>

          {/* Access Form */}
          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="crew-id">Crew ID (Email)</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-stone-400" />
                <Input
                  id="crew-id"
                  name="crew_identifier"
                  type="text"
                  autoComplete="off"
                  value={crewId}
                  onChange={(e) => setCrewId(e.target.value)}
                  placeholder="your@email.com"
                  className="pl-10"
                  data-testid="crew-login-email"
                  data-lpignore="true"
                  data-form-type="other"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="access-code">Access Code</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-stone-400" />
                <Input
                  id="access-code"
                  name="crew_access_code"
                  type="text"
                  autoComplete="off"
                  value={accessCode}
                  onChange={(e) => setAccessCode(e.target.value)}
                  placeholder="••••••••"
                  className="pl-10"
                  style={{ WebkitTextSecurity: 'disc' }}
                  data-testid="crew-login-password"
                  data-lpignore="true"
                  data-form-type="other"
                />
              </div>
              <div className="text-right">
                <button
                  type="button"
                  onClick={() => { setShowForgot(true); setForgotEmail(crewId); }}
                  className="text-xs text-amber-600 hover:text-amber-700 hover:underline font-medium"
                  data-testid="crew-forgot-password-link"
                >
                  Forgot password?
                </button>
              </div>
            </div>

            <div className="text-center text-sm text-amber-600 font-medium">
              Press Enter or click below to access
            </div>

            <button 
              ref={buttonRef}
              type="button"
              className="w-full bg-amber-500 hover:bg-amber-600 text-black h-12 text-lg font-medium rounded-lg flex items-center justify-center gap-2"
              disabled={loading}
              data-testid="crew-login-submit"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-black"></div>
              ) : (
                <>
                  <LogIn className="h-5 w-5" />
                  Access Dashboard
                </>
              )}
            </button>
          </div>

          {/* Info */}
          <div className="mt-8 pt-6 border-t border-stone-100 text-center">
            <p className="text-sm text-stone-500">
              Don't have access? Contact your apiary owner to get your credentials.
            </p>
          </div>
        </div>
      </div>

      {/* Forgot Password Modal */}
      {showForgot && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => { setShowForgot(false); setForgotSent(false); }}>
          <div className="bg-white rounded-2xl shadow-xl p-6 max-w-sm w-full" onClick={e => e.stopPropagation()} data-testid="crew-forgot-modal">
            {forgotSent ? (
              <div className="text-center">
                <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="h-7 w-7 text-green-600" />
                </div>
                <h3 className="text-lg font-bold text-stone-800 mb-2">Check Your Email</h3>
                <p className="text-sm text-stone-500 mb-4">
                  If an account exists for <strong>{forgotEmail}</strong>, we've sent a password reset link.
                </p>
                <Button onClick={() => { setShowForgot(false); setForgotSent(false); }} className="w-full bg-amber-600 hover:bg-amber-700">
                  Back to Login
                </Button>
              </div>
            ) : (
              <>
                <h3 className="text-lg font-bold text-stone-800 mb-1">Forgot Password?</h3>
                <p className="text-sm text-stone-500 mb-4">Enter your crew email and we'll send you a reset link.</p>
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <Input
                    type="email"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="h-12"
                    data-testid="crew-forgot-email-input"
                    autoFocus
                    required
                  />
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" onClick={() => setShowForgot(false)} className="flex-1">Cancel</Button>
                    <Button type="submit" disabled={forgotLoading} className="flex-1 bg-amber-600 hover:bg-amber-700 text-white" data-testid="crew-forgot-submit-btn">
                      {forgotLoading ? 'Sending...' : 'Send Reset Link'}
                    </Button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CrewLogin;
